/**
 * KEEP THE CARET OFF THE BOTTOM EDGE WHILE A COMPOSER GROWS (OSO-604).
 *
 * The composers are auto-growing `contentEditable` boxes with no scrollport of
 * their own: every new line makes the element taller and pushes the caret
 * further down the page. Browsers do scroll a caret back into view on input,
 * but they scroll the MINIMUM amount that makes it technically visible — the
 * line ends up flush against the bottom edge, under the send button, under the
 * browser chrome, and on a phone under the keyboard. That is the state the
 * reporter photographed: the text was on screen, and they were still writing
 * blind.
 *
 * So this asks for a margin rather than for visibility. Below the fold and
 * "one pixel above the fold" get the same answer.
 *
 * ONLY THE BOTTOM EDGE. Growth pushes downwards, which is the whole bug, and
 * the top edge is not free to move: `RichTextInput`'s formatting row is
 * `sticky top-0` inside whichever scrollport it is in (OSO-593), so scrolling a
 * caret to `top + margin` could park it underneath the toolbar — trading a
 * caret hidden by the fold for a caret hidden by B/I/U. Backspacing upwards is
 * left to the browser, which handles it.
 */

/** Breathing room below the caret, in CSS pixels — roughly one more line. */
const CARET_MARGIN = 32;

/**
 * What a caret scroll would have to move: an element with a scrollport of its
 * own, the page, or nothing at all.
 */
type ScrollHost = HTMLElement | "page" | "nothing";

/**
 * Which of those the editor sits in.
 *
 * `overflow-y` alone is not the question — the post composer's dialog body is
 * `overflow-y-auto` whether or not it has overflowed — so a candidate only
 * counts once it has something to scroll.
 *
 * A COMPOSER NEVER SCROLLS ANYTHING OUTSIDE ITSELF (OSO-661). Reaching a
 * `fixed` ancestor ends the walk with "nothing" rather than falling through to
 * the page: a fixed box's coordinates do not move when the document does, so
 * scrolling the document cannot bring the caret anywhere — it just drags
 * whatever is behind the overlay past it. That is the chat dock over the feed,
 * where every character typed scrolled the feed a few pixels down; it is
 * equally the lightbox's comment box and a dialog too short to have overflowed
 * yet.
 *
 * `fixed` ONLY, and the two checks are in this order on purpose. `sticky` is
 * not fixed — it travels with its scroll container and an ancestor scrollport
 * scrolls it normally, so a composer under one of the app's `sticky
 * top-[4.5rem]` wrappers still wants the page. And a node can be both fixed
 * and a scrollport (`fixed inset-0 … overflow-y-auto`, the standard modal, as
 * in ImageCropper): testing the overflow first hands back the scrollport that
 * is right there instead of giving up in front of it.
 */
function scrollHost(el: HTMLElement): ScrollHost {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY, position } = getComputedStyle(node);
    if (
      (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    if (position === "fixed") return "nothing";
  }
  return "page";
}

/**
 * Where the caret is drawn, in client coordinates.
 *
 * A collapsed range has no width, and in a few positions — right after a `<br>`,
 * inside a freshly opened tag — no box at all. The editor plants zero-width
 * spaces to keep the caret paintable in exactly those spots
 * (`lib/richText/editorDom.ts`), so this is rare; when it does happen the
 * bottom of the editor is the right answer anyway, because a caret with no box
 * is one on the last, empty line.
 */
function caretBottom(editor: HTMLElement): number | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  if (!selection.focusNode || !editor.contains(selection.focusNode)) return null;

  const range = selection.getRangeAt(0).cloneRange();
  range.collapse(false);
  const rect = range.getBoundingClientRect();
  if (rect.bottom !== 0 || rect.height !== 0) return rect.bottom;
  return editor.getBoundingClientRect().bottom;
}

/**
 * Scroll just enough that the caret sits at least `CARET_MARGIN` above the
 * bottom of whatever it is scrolling inside. A no-op when it already does.
 */
export function scrollCaretIntoView(editor: HTMLElement): void {
  const bottom = caretBottom(editor);
  if (bottom == null) return;

  const host = scrollHost(editor);
  if (host === "nothing") return;

  /**
   * `visualViewport` is what the on-screen keyboard shrinks; `innerHeight` is
   * not. Reading it is the difference between "visible" and "visible to
   * somebody holding a phone".
   */
  const viewBottom =
    host === "page"
      ? (window.visualViewport
          ? window.visualViewport.height + window.visualViewport.offsetTop
          : window.innerHeight)
      : host.getBoundingClientRect().bottom;

  const delta = bottom + CARET_MARGIN - viewBottom;
  if (delta <= 0) return;

  // Instant, never smooth: this runs on a keystroke, and an animation started
  // by every one of them lags behind the typing it is chasing.
  if (host === "page") {
    window.scrollBy(0, delta);
  } else {
    host.scrollTop += delta;
  }
}
